import { z } from "zod";

import { MetaDataSchema } from "@/schemas/meta-data";
import { ColumnTemplateSchema } from "@/pages/Private/pages/List/schema/column-template";
import { WebhookType } from "@/enum/webhook.enum";
import { ListStatus } from "@/enum/list.enum";

import { WebhookSigningStatusSchema } from "./webhookSigning";

export const WebhookDeleteResponseSchema = z.object({
	message: z.string(),
});

export const CreateWebhookSchema = z.object({
	name: z.string().min(1),
	url: z.string().min(1),
	receivesData: z.boolean(),
	customColumns: z.array(z.string()).optional(),
	/** 1 the legacy lead array, 2 one envelope per delivery (PREC-4251); absent reads as 1. */
	payloadVersion: z.number().int().optional(),
});

/** A list the webhook is attached to (PREC-4253): what on that list sends it a lead. */
export const WebhookAttachedListSchema = z.object({
	listId: z.number(),
	name: z.string(),
	trigger: z.nativeEnum(WebhookType),
	listStatus: z.nativeEnum(ListStatus),
});

export const WebhookSchema = CreateWebhookSchema.extend({
	id: z.number().gt(0),
	createdAt: z.string(),
	columnTemplates: z.array(ColumnTemplateSchema).optional(),
	/** True when `url` hides part of the stored URL; the stored one never reaches the browser. */
	urlMasked: z.boolean().optional(),
	// List attachments (PREC-4253), on the list endpoint only; see `WebhookListsCell`.
	lists: z.array(WebhookAttachedListSchema).optional(),
	privateListCount: z.number().optional(),
	privateListTriggers: z.array(z.nativeEnum(WebhookType)).optional(),
	givenUpDeliveries: z.number().optional(),
	// Delivery health (PREC-4248); see `describeWebhookHealth`.
	lastDeliveryAt: z.string().nullable().optional(),
	lastDeliveredAt: z.string().nullable().optional(),
	lastStatus: z.number().nullable().optional(),
	lastError: z.string().nullable().optional(),
	lastFailureAt: z.string().nullable().optional(),
	failingSince: z.string().nullable().optional(),
	consecutiveFailures: z.number().optional(),
	pausedAt: z.string().nullable().optional(),
	// Signing (PREC-4249); see `WebhookSigningCell`.
	signing: WebhookSigningStatusSchema.optional(),
	ListWebhook: z
		.object({
			id: z.number().optional(),
			listId: z.number(),
			webhookId: z.number(),
			type: z.nativeEnum(WebhookType),
		})
		.optional(),
});

export const UpdateWebhookSchema = WebhookSchema.extend({
	id: z.number().gt(0),
	// Sent only when the user replaced it: the form holds the masked URL, and a save without a URL
	// keeps the stored one (PREC-4253).
	url: z.string().min(1).optional(),
	newCustomColumns: z.array(z.string()).optional(),
	removeCustomColumnIds: z.array(z.number()).optional(),
});

/** The create answer: the webhook, and its signing secret this one time (PREC-4249). */
export const WebhookCreatedSchema = WebhookSchema.extend({
	signingSecret: z.string(),
});

export const WebhookResponseSchema = z.object({
	data: z.array(WebhookSchema),
	meta: MetaDataSchema,
});

export type Webhook = z.infer<typeof WebhookSchema>;
export type WebhookAttachedList = z.infer<typeof WebhookAttachedListSchema>;
export type WebhookResponse = z.infer<typeof WebhookResponseSchema>;
export type WebhookCreated = z.infer<typeof WebhookCreatedSchema>;
export type WebhookDeleteResponse = z.infer<typeof WebhookDeleteResponseSchema>;
export type CreateWebhook = z.infer<typeof CreateWebhookSchema>;
export type UpdateWebhook = z.infer<typeof UpdateWebhookSchema>;
